import authApi from 'api/authApi';
import feApi from 'api/feApi';
import 'assets/styles/keenpages/login-1.css';
import AuthHelper from 'features/Auth/authHelper';
import ForgotPasswordForm from 'features/Auth/components/ForgotPasswordForm';
import ResetPasswordForm from 'features/Auth/components/ResetPasswordForm';
import SignInForm from 'features/Auth/components/SignInForm';
import SignUpForm from 'features/Auth/components/SignUpForm';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import { AnimatePresence } from 'framer-motion';
import { AppDialogHelper } from 'general/components/AppDialog';
import AppResource from 'general/constants/AppResource';
import AppRoute from 'general/constants/AppRoute';
import ToastHelper from 'general/helpers/ToastHelper';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

function AuthBaseScreen(props) {
  // MARK: --- Params ---
  const router = useRouter();
  const { t } = useTranslation();
  const { isLoggingIn, user } = useSelector((state) => state.auth);

  // MARK: --- Functions ---
  const handleNotFoundEmailDialog = (onCloseCallback) => {
    AppDialogHelper.show(
      t('Cảnh báo'),
      t('Email này chưa đăng ký tài khoản'),
      [
        {
          title: t('Tôi đã hiểu'),
          type: 'neutral',
          onClick: () => {
            AppDialogHelper.hide();
            if (onCloseCallback) onCloseCallback();
          },
        },
      ],
      false,
      AppResource.images.dialogIcons.dialogNoticeEmail
    );
  };

  async function handleSubmitForgotPassword(values) {
    try {
      const res = await authApi.forgotPassword(values.email);
      const { result } = res;
      if (result === 'success') {
        ToastHelper.showSuccess(
          'Vui lòng kiểm tra hòm thư và làm theo hướng dẫn để thực hiện đặt lại mật khẩu'
        );
        return 'success';
      }
    } catch (error) {
      const msg = error?.response?.data?.reason ?? '';
      if (msg === 'Không tìm thấy tài khoản') {
        handleNotFoundEmailDialog();
      } else {
        ToastHelper.showError(msg || 'Đã xảy ra lỗi, vui lòng thử lại sau');
      }
      return 'error';
    }
  }


  function handleCancelForgotPassword() {
    router.navigate(-1);
  }

  const handleRegisterSuccessDialog = () => {
    AppDialogHelper.show(
      t('Đăng ký thành công'),
      t('Đăng ký tài khoản thành công, vui lòng kiểm tra hòm thư để kích hoạt tài khoản'),
      [
        {
          title: t('Đóng'),
          type: 'neutral',
          onClick: () => {
            AppDialogHelper.hide();
          },
        },
        {
          title: t('signIn'),
          type: 'success',
          onClick: async () => {
            AppDialogHelper.hide();
            router.navigate(`${AppRoute.auth}/sign-in`);
          },
        },
      ],
      false,
      AppResource.images.dialogIcons.dialogSuccess
    );
  };

  const handleSubmitSignUp = async (values) => {
    const { result, reason } = await DealerHelper.callApiRegisterAgencyAccount(values);
    if (result === 'success') {
      handleRegisterSuccessDialog();
    } else {
      ToastHelper.showError(reason);
    }
  };

  function handleCancelSignUp() {
    router.navigate(-1);
  }

  const handleSignIn = (params) => {
    AuthHelper.callApiLogIn(params);
  };

  // MARK: --- Hooks ---
  useEffect(() => {
    if (!isLoggingIn && !_.isEmpty(user)) {
      // dang nhap thanh cong --> chuyen sang man dashboard
      router.navigate('/');
    }
  }, [isLoggingIn, user]);

  return (
    <div className="d-flex flex-column flex-root overflow-hidden">
      <div className="bg-white d-flex flex-column flex-column-fluid flex-lg-row login login-1 login-signin-on min-vh-100">
        {/* aside */}
        <div
          className="login-aside d-flex flex-column"
          style={{
            backgroundImage: `url(${AppResource.images.evg.bgAuthForm})`,
            backgroundSize: 'cover',
          }}
        >
          {/* aside top */}
          <div className="d-flex flex-column h-100">
            <div className="h-50 d-flex align-items-end justify-content-center mb-5">
              <a href="#">
                <img alt="logo" src={AppResource.ic.icLogoAuth} style={{ width: 362, height: 68 }}
              />
              </a>
            </div>
            <div
              className="aside-img d-flex w-100 flex-row-fluid bgi-no-repeat bgi-position-y-bottom bgi-position-x-bottom"
              style={{
                backgroundImage: `url(${router.location.pathname === `${AppRoute.auth}/sign-in`
                  ? AppResource.images.evg.vehicleBg
                  : AppResource.images.evg.motorBg
                  })`,
                backgroundSize: '100% auto',
              }}
            ></div>
          </div>
        </div>

        {/* content */}
        <div className="login-content flex-row-fluid d-flex flex-column justify-content-center position-relative overflow-auto p-7 mx-auto">
          {/* content body */}
          <div className="d-flex flex-column-fluid flex-center">
            <AnimatePresence mode="wait">
              <Routes location={router.location} key={router.location.pathname}>
                <Route path="" element={<Navigate to="sign-in" />} />
                <Route
                  path="sign-in"
                  element={
                    <SignInForm
                      onSignIn={(params) => {
                        handleSignIn(params);
                      }}
                    />
                  }
                />
                <Route
                  path="sign-up-sale"
                  element={
                    <SignUpForm
                      role="SALE"
                      onSubmit={handleSubmitSignUp}
                      onCancel={handleCancelSignUp}
                    />
                  }
                />
                <Route
                  path="sign-up-sale-only"
                  element={
                    <SignUpForm
                      role="SALE"
                      onSubmit={handleSubmitSignUp}
                      onCancel={handleCancelSignUp}
                      isSaleOnly={true}
                    />
                  }
                />
                <Route
                  path="sign-up"
                  element={
                    <SignUpForm
                      onSubmit={handleSubmitSignUp}
                      onCancel={handleCancelSignUp}
                    />
                  }
                />
                <Route
                  path="sign-up-investor"
                  element={
                    <SignUpForm
                      role="INVESTOR"
                      onSubmit={handleSubmitSignUp}
                      onCancel={handleCancelSignUp}
                    />
                  }
                />
                <Route
                  path="sign-up-group"
                  element={
                    <SignUpForm
                      role="GROUP"
                      onSubmit={handleSubmitSignUp}
                      onCancel={handleCancelSignUp}
                    />
                  }
                />
                <Route
                  path="forgot-password"
                  element={
                    <ForgotPasswordForm
                      onSubmit={handleSubmitForgotPassword}
                      onCancel={handleCancelForgotPassword}
                    />
                  }
                />
                <Route path="reset-password" element={<ResetPasswordForm />} />
              </Routes>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthBaseScreen;
